/**
 * Reviews carousel (sections/reviews-carousel.liquid)
 * Fetches reviews from the Reviews.io public timeline API when the section nears the viewport,
 * then renders them into the section's templates and hands the slides to <slider-component>.
 * Review text is always set with textContent, never as HTML.
 */
if (!customElements.get("reviews-carousel")) {
  customElements.define(
    "reviews-carousel",
    class ReviewsCarousel extends HTMLElement {
      static API = "https://api.reviews.io/timeline/data";

      // Some imported reviews store empty fields as a literal "" (two quote marks).
      static clean(value) {
        return String(value ?? "").replace(/^["'\s]+|["'\s]+$/g, "");
      }

      connectedCallback() {
        if (this.observer || this.dataset.state !== "loading") return;
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: "400px 0px" },
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async load() {
        const params = new URLSearchParams({
          type: this.dataset.type,
          store: this.dataset.store,
          sort: "date_desc",
          page: "1",
          per_page: this.dataset.count || "20",
          must_have_comments: "true",
          enable_avatars: "false",
          lang: "en",
        });
        if (this.dataset.sku) params.set("sku", this.dataset.sku);
        if (this.hasAttribute("data-include-imported")) params.set("include_local_reviews", "1");

        try {
          const response = await fetch(`${ReviewsCarousel.API}?${params}`);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();
          const reviews = (data.timeline || [])
            .map((item) => item._source)
            .filter((review) => review && ReviewsCarousel.clean(review.comments));
          if (!reviews.length) throw new Error("No reviews");
          this.render(data.stats || {}, reviews);
        } catch (error) {
          this.dataset.state = "empty";
          this.closest(".section-reviews-carousel").hidden = true;
        }
      }

      render(stats, reviews) {
        this.strings = JSON.parse(this.querySelector("[data-strings]").textContent);
        const layout = this.querySelector("template[data-layout]").content.cloneNode(true);

        const average = parseFloat(stats.average_rating) || 0;
        const total = parseInt(stats.review_count, 10) || reviews.length;
        layout.querySelector("[data-word]").textContent = this.ratingWord(average);
        layout.querySelector("[data-stars]").append(this.stars(average));
        layout.querySelector("[data-average]").textContent = this.strings.average.replace("[rating]", average.toFixed(2));
        layout.querySelector("[data-total]").textContent = this.strings.total.replace(
          "[count]",
          new Intl.NumberFormat(document.documentElement.lang || "en").format(total),
        );

        const slides = layout.querySelector("[data-slides]");
        const cardTemplate = this.querySelector("template[data-card]");
        reviews.forEach((review, index) => {
          const card = cardTemplate.content.cloneNode(true);
          // slider.js only counts slides whose id starts with "Slide-".
          card.querySelector("li").id = `${slides.id.replace("Slider-", "Slide-")}-${index + 1}`;
          card.querySelector("[data-author]").textContent = ReviewsCarousel.clean(review.author) || this.strings.anonymous;
          card.querySelector("[data-stars]").append(this.stars(Number(review.rating) || 0));
          card.querySelector("[data-badge]").textContent =
            review.type === "store_third_party_review" && review.source
              ? this.strings.thirdParty.replace("[source]", review.source)
              : this.strings.verified;
          this.setOptional(card.querySelector("[data-product]"), ReviewsCarousel.clean(review.product_name));
          this.setOptional(card.querySelector("[data-title]"), ReviewsCarousel.clean(review.review_title));
          card.querySelector("[data-comment]").textContent = review.comments.trim();
          card.querySelector("[data-meta]").textContent = [ReviewsCarousel.clean(review.address), this.timeAgo(review.date_created)]
            .filter(Boolean)
            .join(", ");
          slides.append(card);
        });

        // Appending the layout upgrades <slider-component> with its slides already in place.
        this.append(layout);
        this.dataset.state = "loaded";
      }

      setOptional(element, text) {
        if (!text) return;
        element.textContent = text;
        element.hidden = false;
      }

      ratingWord(average) {
        const [excellent, great, good, fair, poor] = this.strings.words;
        if (average >= 4.5) return excellent;
        if (average >= 4) return great;
        if (average >= 3.5) return good;
        if (average >= 2.5) return fair;
        return poor;
      }

      // Same rounding as snippets/rating.liquid: a half star between .3 and .7.
      stars(rating) {
        const wrapper = document.createElement("span");
        wrapper.className = "rating";
        wrapper.setAttribute("role", "img");
        wrapper.setAttribute("aria-label", this.strings.stars.replace("[rating]", rating));

        let full = Math.floor(rating);
        const fraction = rating - full;
        let half = false;
        if (fraction >= 0.7) full += 1;
        else if (fraction > 0.3) half = true;

        for (let i = 0; i < 5; i++) {
          const kind = i < full ? "full" : i === full && half ? "half" : "empty";
          wrapper.append(this.querySelector(`template[data-star-${kind}]`).content.cloneNode(true));
        }
        return wrapper;
      }

      // Reviews.io dates are UTC, formatted "YYYY-MM-DD HH:MM:SS".
      timeAgo(dateString) {
        if (!dateString) return "";
        const date = new Date(`${dateString.replace(" ", "T")}Z`);
        if (Number.isNaN(date.getTime())) return "";
        const seconds = (date.getTime() - Date.now()) / 1000;
        const units = [
          ["year", 31536000],
          ["month", 2592000],
          ["day", 86400],
          ["hour", 3600],
          ["minute", 60],
        ];
        const format = new Intl.RelativeTimeFormat(document.documentElement.lang || "en", { numeric: "auto" });
        for (const [unit, size] of units) {
          if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit);
        }
        return format.format(0, "minute");
      }
    },
  );
}
